/**
 * ORDEM DO FLUXO — uma regra só para todas as abas (19/10: "a ordem dos bots não
 * respeita a ordem que coloquei lá na tela inicial — o Auditor de Impostos está
 * como o último").
 *
 * A ordem que o dono organiza mora em dois lugares, e os dois são lidos aqui:
 *   1. a ordem dos FLUXOS (departamentos): o campo `ordem` gravado no cadastro;
 *      sem ele, Fiscal → Contábil → resto → "sem departamento";
 *   2. a ordem DENTRO do fluxo: a sequência dos grupos da esteira (`/esteira`, já
 *      linearizada pela cadeia `depende_de`) e, no grupo, a ordem dos bots.
 *
 * Antes o Resumo ordenava pela cadeia bot→bot (`dep_bot`) e pela ordem da
 * cobertura — nenhuma das duas é a da tela do Processos, e o resultado divergia.
 */

import type { IDepartamento, IEsteiraResposta } from '../api';

/** Ordem padrão dos fluxos enquanto ninguém gravou uma no cadastro. */
export const rankPadraoDoFluxo = (nome: string): number => {
  if (/^fiscal/i.test(nome)) return 0;
  if (/^cont[aá]bil/i.test(nome)) return 1;
  // Sem departamento ("Bots sem grupo") sempre por último.
  return nome ? 2 : 3;
};

/**
 * Os nomes dos fluxos na ordem da tela. `nomes` na ordem em que chegaram (é o
 * desempate); `ordemLocal` é a ordem otimista do Processos enquanto grava.
 */
export const ordenarFluxos = (
  nomes: string[],
  departamentos: IDepartamento[] | undefined,
  ordemLocal: string[] | null = null,
): string[] => {
  const porNome = new Map((departamentos ?? []).map(d => [d.nome, d]));
  const ordemDoServidor = (nome: string): number | null => porNome.get(nome)?.ordem ?? null;
  return nomes
    .map((nome, i) => ({ nome, i }))
    .sort((a, b) => {
      if (ordemLocal) {
        const ia = ordemLocal.indexOf(a.nome);
        const ib = ordemLocal.indexOf(b.nome);
        if (ia >= 0 && ib >= 0) return ia - ib;
      }
      const oa = ordemDoServidor(a.nome);
      const ob = ordemDoServidor(b.nome);
      if (oa !== null && ob !== null && oa !== ob) return oa - ob;
      if (oa !== null && ob === null) return -1;
      if (oa === null && ob !== null) return 1;
      return rankPadraoDoFluxo(a.nome) - rankPadraoDoFluxo(b.nome) || a.i - b.i;
    })
    .map(x => x.nome);
};

export interface OrdemDoFluxo {
  /** Fluxos (departamentos) na ordem da tela; "" = sem departamento. */
  fluxos: string[];
  /** Posição global do bot: fluxo → grupo na esteira → bot no grupo. */
  posicaoDoBot: Map<string, number>;
  /** Fluxo de cada bot (o primeiro em que ele aparece). */
  fluxoDoBot: Map<string, string>;
  /**
   * TODOS os fluxos de cada bot, na ordem da tela. Bot em N grupos de áreas
   * diferentes (ex.: BOT_EFD_REINF no Fiscal SN, LP e LR) pertence às N — usar só
   * o primeiro escondia o que ele fez nas outras áreas (24/10).
   */
  fluxosDoBot: Map<string, string[]>;
  /** Grupos de cada fluxo na sequência da esteira, com seus bots. */
  gruposDoFluxo: Map<string, Array<{ nome: string; bots: string[] }>>;
}

export const ordemDoFluxo = (
  esteira: IEsteiraResposta | undefined,
  departamentos: IDepartamento[] | undefined,
): OrdemDoFluxo => {
  const linhas = esteira?.linhas ?? [];
  const nomes: string[] = [];
  linhas.forEach(l => {
    const n = l.departamento ?? '';
    if (!nomes.includes(n)) nomes.push(n);
  });
  const fluxos = ordenarFluxos(nomes, departamentos);
  const gruposDoFluxo = new Map<string, Array<{ nome: string; bots: string[] }>>();
  linhas.forEach(l => {
    const n = l.departamento ?? '';
    const lista = gruposDoFluxo.get(n) ?? [];
    l.grupos.forEach(g => lista.push({ nome: g.nome, bots: g.bots ?? [] }));
    gruposDoFluxo.set(n, lista);
  });
  const posicaoDoBot = new Map<string, number>();
  const fluxoDoBot = new Map<string, string>();
  const fluxosDoBot = new Map<string, string[]>();
  let i = 0;
  fluxos.forEach(f => {
    (gruposDoFluxo.get(f) ?? []).forEach(g =>
      g.bots.forEach(bot => {
        const doBot = fluxosDoBot.get(bot) ?? [];
        if (!doBot.includes(f)) fluxosDoBot.set(bot, [...doBot, f]);
        if (posicaoDoBot.has(bot)) return;
        posicaoDoBot.set(bot, i);
        fluxoDoBot.set(bot, f);
        i += 1;
      }),
    );
  });
  return { fluxos, posicaoDoBot, fluxoDoBot, fluxosDoBot, gruposDoFluxo };
};
